/**
 * Heuro Cinema - Language Selection
 * Handles the opening language selection sequence.
 */

import type { PropertyConfig } from '../types/content';

interface LanguagePhrase {
  code: string;
  label: string;
  phrase: string;
}

let languageSelected = false;
let onLanguageSelectedCallback: ((lang: string) => void) | null = null;

export function setOnLanguageSelected(callback: (lang: string) => void): void {
  onLanguageSelectedCallback = callback;
}

export function isLanguageSelected(): boolean {
  return languageSelected;
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function initLanguageSelection(
  languages: string[],
  fetchConfigForLang: (lang: string) => Promise<PropertyConfig>
): Promise<void> {
  const phrasesContainer = document.getElementById('langPhrases');
  const langHint = document.getElementById('langHint');
  const themeToggle = document.getElementById('themeToggle');

  if (!phrasesContainer) return;

  // Fetch phrases for each language
  const phrases: LanguagePhrase[] = [];
  for (const lang of languages) {
    try {
      const config = await fetchConfigForLang(lang);
      phrases.push({
        code: lang,
        label: config.i18n.meta.label,
        phrase: config.i18n.language_select.phrase,
      });
    } catch (e) {
      console.error(`Failed to fetch config for ${lang}:`, e);
    }
  }

  // Create phrase elements
  phrases.forEach(p => {
    const el = document.createElement('div');
    el.className = 'lang-phrase';
    el.dataset.lang = p.code;
    el.dataset.langLabel = p.label;
    el.textContent = p.phrase;
    phrasesContainer.appendChild(el);
  });

  const phraseElements = phrasesContainer.querySelectorAll('.lang-phrase');

  // Animate phrases in
  await wait(500);
  for (let i = 0; i < phraseElements.length; i++) {
    phraseElements[i].classList.add('visible');
    await wait(600);
  }

  // Make selectable
  await wait(400);
  phraseElements.forEach(p => p.classList.add('selectable'));
  langHint?.classList.add('visible');
  themeToggle?.classList.add('visible');

  // Set up click handlers
  phraseElements.forEach(phrase => {
    phrase.addEventListener('click', () => {
      if (phrase.classList.contains('selectable') && !languageSelected) {
        selectLanguage(phrase as HTMLElement, phraseElements);
      }
    });
  });
}

async function selectLanguage(
  selectedPhrase: HTMLElement,
  allPhrases: NodeListOf<Element>
): Promise<void> {
  languageSelected = true;
  const lang = selectedPhrase.dataset.lang || 'en';

  const langHint = document.getElementById('langHint');
  const langSelection = document.getElementById('langSelection');

  // Animate selection
  langHint?.classList.remove('visible');
  selectedPhrase.classList.remove('selectable');
  selectedPhrase.classList.add('selected');

  // Fade out other phrases
  allPhrases.forEach(p => {
    if (p !== selectedPhrase) {
      p.classList.remove('selectable', 'visible');
      p.classList.add('fade-out');
    }
  });

  await wait(700);

  // Chosen animation
  selectedPhrase.classList.remove('selected');
  selectedPhrase.classList.add('chosen');

  await wait(350);

  // Hide language selection
  langSelection?.classList.add('hidden');

  // Trigger callback
  if (onLanguageSelectedCallback) {
    onLanguageSelectedCallback(lang);
  }
}